<template>
  <div class="bg-surface-default-low py-72 md:py-96">
    <RevContainer>
      <div class="space-y-8 md:block mb-32 md:mb-64">
        <h2 class="heading-1">
          {{ i18n(translations.title) }}
        </h2>

        <p class="body-1 text-static-default-low">
          {{ i18n(translations.subtitle) }}
        </p>
      </div>

      <!-- Desktop layout -->
      <div class="hidden md:flex md:items-center md:gap-32">
        <div class="flex min-w-[335px]">
          <RevCard
            class="flex-1 !bg-surface-default-hi border border-static-default-mid py-40 px-24"
          >
            <div class="space-y-24">
              <RevIllustration
                alt=""
                :height="22"
                src="/img/telco-partnership/verizon.svg"
                :width="98"
              />

              <div class="space-y-12">
                <div class="flex items-center justify-between gap-8">
                  <span class="body-2-bold">
                    {{ i18n(translations.baselinePhoneCostLabel) }}
                  </span>
                  <span class="body-1">
                    {{ i18n(translations.baselinePhoneCostValue) }}
                  </span>
                </div>

                <div class="flex items-center justify-between gap-8">
                  <span class="body-2-bold">
                    {{ i18n(translations.baselinePlanCostLabel) }}
                  </span>
                  <span class="body-1">
                    {{ i18n(translations.baselinePlanCostValue) }}
                  </span>
                </div>

                <RevDivider />

                <div class="flex items-center justify-between gap-8">
                  <span class="body-2-bold">
                    {{ i18n(translations.baselineTotalCostLabel) }}
                  </span>
                  <span class="punchline text-[2.5rem] leading-[2.5rem]">
                    {{ i18n(translations.baselineTotalCostValue) }}
                  </span>
                </div>
              </div>
            </div>
          </RevCard>
        </div>

        <div class="flex flex-col gap-16 self-stretch items-center">
          <div class="bg-static-default-hi grow w-1" />
          <div class="body-1 text-static-default-low">
            {{ i18n(translations.versus) }}
          </div>
          <div class="bg-static-default-hi grow w-1" />
        </div>

        <div class="flex">
          <div class="flex-1 space-y-16">
            <p class="heading-3">
              {{ i18n(translations.comparisonTitle) }}
            </p>

            <div
              class="hidden md:grid md:grid-cols-[minmax(140px,250px)_1fr_1fr_1fr] md:gap-x-16 md:pb-8"
            >
              <span class="caption-bold text-static-default-low text-center" />
              <span class="caption-bold text-static-default-low text-center">
                {{ i18n(translations.colPhoneCost) }}
              </span>
              <span class="caption-bold text-static-default-low text-center">
                {{ i18n(translations.colPlanCost) }}
              </span>
              <span class="caption-bold text-static-default-low text-center">
                {{ i18n(translations.colTotalCost) }}
              </span>
            </div>

            <ComparisonRow
              logo-src="/img/visible-by-verizon/visible-by-verizon-logo-square.svg"
              :name="i18n(translations.visibleName)"
              :phone-cost="i18n(translations.visiblePhoneCost)"
              :plan-cost="i18n(translations.visiblePlanCost)"
              :savings-amount="i18n(translations.visibleSavingsAmount)"
              :total-cost="i18n(translations.visibleTotalCost)"
            />

            <ComparisonRow
              logo-src="/img/telco-partnership/us-mobile.svg"
              :name="i18n(translations.usMobileName)"
              :phone-cost="i18n(translations.usMobilePhoneCost)"
              :phone-cost-strikethrough="
                i18n(translations.usMobilePhoneCostStrikethrough)
              "
              :phone-discount-label="i18n(translations.usMobilePhoneDiscount)"
              :plan-cost="i18n(translations.usMobilePlanCost)"
              :savings-amount="i18n(translations.usMobileSavingsAmount)"
              :total-cost="i18n(translations.usMobileTotalCost)"
            />

            <ComparisonRow
              logo-src="/img/noble/noble-logo-tiny-white.svg"
              :name="i18n(translations.nobleName)"
              :phone-cost="i18n(translations.noblePhoneCost)"
              :phone-cost-strikethrough="
                i18n(translations.noblePhoneCostStrikethrough)
              "
              :phone-discount-label="i18n(translations.noblePhoneDiscount)"
              :plan-cost="i18n(translations.noblePlanCost)"
              :savings-amount="i18n(translations.nobleSavingsAmount)"
              :total-cost="i18n(translations.nobleTotalCost)"
            />
          </div>
        </div>
      </div>

      <!-- Mobile layout -->
      <div class="md:hidden">
        <div class="border border-static-default-low rounded-lg">
          <MobileComparisonRow
            :phone-cost="i18n(translations.visiblePhoneCost)"
            :plan-cost="i18n(translations.visiblePlanCost)"
            :savings-amount="i18n(translations.visibleSavingsAmount)"
            :total-cost="i18n(translations.visibleTotalCost)"
          >
            <template #header>
              <div class="flex items-center gap-8">
                <RevIllustration
                  alt=""
                  class="size-24 shrink-0"
                  :height="24"
                  src="/img/visible-by-verizon/visible-by-verizon-logo-square.svg"
                  :width="24"
                />
                <span class="body-2-bold">{{
                  i18n(translations.visibleName)
                }}</span>
              </div>
            </template>
          </MobileComparisonRow>

          <RevDivider />

          <MobileComparisonRow
            :discount-label="i18n(translations.usMobilePhoneDiscount)"
            :phone-cost="i18n(translations.usMobilePhoneCost)"
            :plan-cost="i18n(translations.usMobilePlanCost)"
            :savings-amount="i18n(translations.usMobileSavingsAmount)"
            :total-cost="i18n(translations.usMobileTotalCost)"
          >
            <template #header>
              <div class="flex items-center gap-8">
                <RevIllustration
                  alt=""
                  class="size-24 shrink-0"
                  :height="24"
                  src="/img/telco-partnership/us-mobile.svg"
                  :width="24"
                />
                <span class="body-2-bold">{{
                  i18n(translations.usMobileName)
                }}</span>
              </div>
            </template>
          </MobileComparisonRow>

          <RevDivider />

          <MobileComparisonRow
            :discount-label="i18n(translations.noblePhoneDiscount)"
            :phone-cost="i18n(translations.noblePhoneCost)"
            :plan-cost="i18n(translations.noblePlanCost)"
            :savings-amount="i18n(translations.nobleSavingsAmount)"
            :total-cost="i18n(translations.nobleTotalCost)"
          >
            <template #header>
              <div class="flex items-center gap-8">
                <RevIllustration
                  alt=""
                  class="size-24 shrink-0"
                  :height="24"
                  src="/img/noble/noble-logo-tiny-white.svg"
                  :width="24"
                />
                <span class="body-2-bold">{{
                  i18n(translations.nobleName)
                }}</span>
              </div>
            </template>
          </MobileComparisonRow>

          <RevDivider />

          <MobileComparisonRow
            is-contract
            :phone-cost="i18n(translations.mobileBaselinePhoneCost)"
            :plan-cost="i18n(translations.mobileBaselinePlanCost)"
            :savings-amount="''"
            :total-cost="i18n(translations.mobileBaselineTotalCost)"
          >
            <template #header>
              <RevIllustration
                :alt="i18n(translations.mobileBaselineName)"
                :height="22"
                src="/img/telco-partnership/verizon.svg"
                :width="98"
              />
            </template>
          </MobileComparisonRow>
        </div>
      </div>

      <p class="caption text-static-default-low md:block mt-32 md:mt-64">
        {{ i18n(translations.disclaimer) }}
      </p>
    </RevContainer>
  </div>
</template>

<script setup lang="ts">
import { useI18n } from '@backmarket/nuxt-module-i18n/useI18n'
import { RevCard } from '@ds/components/Card'
import { RevContainer } from '@ds/components/Container'
import { RevDivider } from '@ds/components/Divider'
import { RevIllustration } from '@ds/components/Illustration'

import translations from './SavingsComparisonSection.translations'
import ComparisonRow from './components/ComparisonRow/ComparisonRow.vue'
import MobileComparisonRow from './components/MobileComparisonRow/MobileComparisonRow.vue'

const i18n = useI18n()
</script>
